/**
 * Command K — when the palette may ask the assistant on its own.
 *
 * Pure, and separated from the component for the same reason the Esc ladder
 * and the keymap are: this is a rule with real money attached, and a rule with
 * money attached should be inspectable without a TestBed.
 *
 * THE BUG THIS EXISTS TO PREVENT
 *
 * The original condition was "the ask row is the only row, and the query is
 * not the one we last asked". Every keystroke produces a different query, so
 * typing "can you book me a desk for tomorrow" fired FIVE separate model
 * calls — one each for "tomor", "tomorr", "tomorro" and the finished
 * sentence — and appended a turn to the visible conversation for every one of
 * them. With the assistant unreachable, that was five stacked error bubbles.
 *
 * Two guards, and they answer different questions:
 *
 *  - **Debounce** (the caller's job — this file has no timers) answers "has
 *    the user finished typing?"
 *  - **`failedThisSession`** answers "is it worth asking at all?" One failure
 *    is enough: without it a dead or misconfigured assistant is re-dialled at
 *    every pause, which is exactly what a local environment with no AI backend
 *    looks like. An EXPLICIT ask clears it, because that is the user saying
 *    "try anyway".
 */

export interface CmdkAutoAskSnapshot {
  /** The palette is on screen. */
  readonly visible: boolean;
  /** This tenant has AI at all. */
  readonly aiEnabled: boolean;
  /**
   * The ask row is the ONLY selectable row. `buildRows` guarantees this shape
   * for the zero-result case, and it is what makes the auto-ask defensible:
   * there is nothing else on screen to choose instead.
   */
  readonly askIsOnlyRow: boolean;
  /** The trimmed, prefix-stripped query. */
  readonly query: string;
  /** The query the ask service last sent, or '' if none. */
  readonly lastAsked: string;
  /** An automatic ask has already failed since the palette opened. */
  readonly failedThisSession: boolean;
  /**
   * A search is still in flight. While Spaces or People are loading, the ask
   * row is the only SELECTABLE row (the rest are skeletons), which used to
   * read as "zero results" — so a slow search billed a model call for a
   * question the results were about to answer. Optional so older callers
   * keep their behaviour.
   */
  readonly searching?: boolean;
}

/**
 * Should the palette ask on the user's behalf?
 *
 * The caller is expected to have debounced already — this returns the same
 * answer whether the user stopped typing a second ago or an hour ago.
 */
export function shouldAutoAsk(snapshot: CmdkAutoAskSnapshot): boolean {
  if (!snapshot.visible) return false;
  if (!snapshot.aiEnabled) return false;
  if (!snapshot.askIsOnlyRow) return false;
  if (!snapshot.query) return false;
  // Already answered this exact question. Re-asking would bill a second call
  // for a reply that is still on screen — and this is also what stops the Esc
  // ladder's dismiss rung from re-arming the answer it just dismissed.
  if (snapshot.query === snapshot.lastAsked) return false;
  if (snapshot.failedThisSession) return false;
  if (snapshot.searching) return false;
  return true;
}
